import React, { useState, useEffect } from 'react';
import { api } from '../config/api';
import OTPEntryPopup from './OTPEntryPopup';
import './RegistrationPopup.css';

/* -------------------- DATE HELPERS -------------------- */

// Max date should be TODAY (no restriction in picker)
const getToday = () => {
  return new Date().toISOString().split('T')[0];
};

// Calculate age from DOB
const calculateAge = (dob) => {
  const today = new Date();
  const birthDate = new Date(dob);

  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();

  if (
    monthDiff < 0 ||
    (monthDiff === 0 && today.getDate() < birthDate.getDate())
  ) {
    age--;
  }
  return age;
};

const RegistrationPopup = ({
  isOpen,
  onClose,
  onRegistrationComplete,
  phoneNumber
}) => {

  /* -------------------- STATE -------------------- */

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    gender: '',
    dateOfBirth: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [showOTPPopup, setShowOTPPopup] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);


  const [timeRemaining, setTimeRemaining] = useState(6 * 60 + 14);

  /* -------------------- EFFECTS -------------------- */

  // Prefill phone number
  useEffect(() => {
    if (phoneNumber) {
      setFormData(prev => ({
        ...prev,
        phone: phoneNumber
      }));
    }
  }, [phoneNumber]);

  // Countdown timer
  useEffect(() => {
    if (!isOpen) return;

    const timer = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 0) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isOpen]);

  /* -------------------- HELPERS -------------------- */

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')} Mins Remaining`;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  /* -------------------- SUBMIT (VALIDATE + SEND OTP) -------------------- */

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      !formData.name ||
      !formData.phone ||
      !formData.email ||
      !formData.gender ||
      !formData.dateOfBirth
    ) {
      setError('Please fill in all required fields');
      return;
    }

    // 🔥 AGE VALIDATION (ONLY ON SUBMIT)
    const age = calculateAge(formData.dateOfBirth);
    if (age < 18) {
      setError('You must be at least 18 years old to register');
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);

      // ✅ SEND OTP FOR REGISTRATION
      await api.sendOTP(formData.phone, 'register', {
        email: formData.email,
        name: formData.name,
      });

      setShowOTPPopup(true);
    } catch (err) {
      console.error('OTP send error:', err);
      setError(err.message || 'Failed to send OTP');
    } finally {
      setIsSubmitting(false);
    }
  };

  /* -------------------- OTP VERIFIED -------------------- */

  const handleOTPVerified = async () => {
    try {
      setIsSubmitting(true);
  
      const response = await api.registerUser({
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        gender: formData.gender,
        dateOfBirth: formData.dateOfBirth
      });
  
   
  
      const userData = response.data || response; 
  
      const loggedInUser = {
        userId: userData.userId,
        id: userData.userId,
        name: userData.name,
        email: userData.email,
        phone: userData.mobile || userData.phone,
        mobile: userData.mobile || userData.phone,
        verified: true,
      };
  
      onRegistrationComplete(loggedInUser);
      
      setShowOTPPopup(false);
      onClose();
  
    } catch (err) {
      console.error('❌ Error in handleOTPVerified:', err);
      setError(err.message || 'Registration failed');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  if (!isOpen) return null;

  /* -------------------- RENDER -------------------- */

  return (
    <div className="registration-overlay">
      <div className="registration-popup">

        {/* Header */}
        <div className="popup-header">
          <div className="timer-display">
            {formatTime(timeRemaining)}
          </div>

          <button
            type="button"
            className="popup-close-btn"
            onClick={onClose}
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="popup-content">
          <h2 className="popup-title">Let's get to know you better</h2>

          {/* <div className="autofill-section">
            <span className="autofill-text">Autofill information with:</span>
            <button
              type="button"
              className="google-autofill-btn"
              onClick={handleGoogleAutofill}
            >
              <div className="google-logo">G</div>
              Sign up with Google
            </button>
          </div> */}

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="registration-form">

            <div className="form-group">
              <input
                type="text"
                name="name"
                placeholder="Name"
                value={formData.name}
                onChange={handleInputChange}
                className="form-input"
                required
                disabled={isSubmitting}
              />
            </div>

            <div className="form-group">
              <input
                type="tel"
                name="phone"
                placeholder="Phone"
                value={formData.phone}
                className="form-input phone-prefilled"
                readOnly
              />
            </div>

            <div className="form-group">
              <input
                type="email"
                name="email"
                placeholder="Email"
                value={formData.email}
                onChange={handleInputChange}
                className="form-input"
                required
                disabled={isSubmitting}
              />
            </div>

            <div className="form-group">
              <select
                name="gender"
                value={formData.gender}
                onChange={handleInputChange}
                className="form-select"
                required
                disabled={isSubmitting}
              >
                <option value="">Gender</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
                <option value="Prefer not to say">Prefer not to say</option>
              </select>
            </div>

            {/* DOB — NO 18+ restriction in picker */}
            <div className="form-group">
            <input
  type="date"
  name="dateOfBirth"
  value={formData.dateOfBirth}
  onChange={handleInputChange}
  className="form-input"
  max={new Date().toISOString().split('T')[0]} // prevent future date
  required
  disabled={isSubmitting}
/>
            </div>

            <div className="form-actions">
              <button type="submit" className="proceed-btn" disabled={isSubmitting}>
                {isSubmitting ? 'Sending OTP...' : 'Proceed'}
              </button>

              <button
                type="button"
                className="cancel-btn"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* OTP POPUP */}
      <OTPEntryPopup
        isOpen={showOTPPopup}
        onClose={() => setShowOTPPopup(false)}
        phoneNumber={formData.phone}
        formData={formData}
        flow="register" 
        onOTPVerified={handleOTPVerified}
      />

      {showSuccess && (
  <div className="registration-success">
    <div className="success-icon">🎉</div>
    <h2>Registration Successful!</h2>
    <p>Welcome to My Cinemas</p>
  </div>
)}

    </div>
  );
};

export default RegistrationPopup;
